// @ts-check
// @ts-ignore - js-cookie doesn't have type definitions
import Cookies from 'js-cookie'
import React from 'react'
import { API_URL } from '../mdc_modules/mdcApiFetch'
import { VITE_PROSEARCH_VERSION } from '../mdc_modules/mdcConsts'

/** @typedef {{ loading: boolean, authenticated: boolean, userSettings: Record<string, any>, error?: any, reload: () => Promise<void> }} AuthContextValue */

/** @type {React.Context<AuthContextValue>} */
export const AuthContext = React.createContext(
  /** @type {AuthContextValue} */ ({
    loading: true,
    authenticated: false,
    userSettings: {},
    error: undefined,
    reload: async () => undefined,
  })
)

/** @typedef {{ children: React.ReactNode }} Props */

/**
 * @param {Props} props
 * @returns {React.ReactElement}
 */
function AuthContextProvider({ children }) {
  const [loading, setLoading] = React.useState(true)
  const [error, setError] = React.useState(/** @type {any} */ (undefined))
  const [authenticated, setAuthenticated] = React.useState(false)
  const [userSettings, setUserSettings] = React.useState({})

  /**
   * @returns {boolean}
   */
  const shouldLoadSettings = () => {
    const mdcCookie = Cookies.get('MDC_LF')
    return mdcCookie || window.location.hostname === 'localhost'
  }

  React.useEffect(() => {
    const abortController = new AbortController()

    const loadSettings = async () => {
      setLoading(true)
      try {
        const response = await fetch(API_URL + '?getUserSettings=1', {
          credentials: 'include',
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            Settings: { getUserSettings: true, react_app_version: VITE_PROSEARCH_VERSION },
          }),
          signal: abortController.signal,
        })

        if (!response.ok) {
          throw new Error(`Failed to load user settings: ${response.status}`)
        }

        const data = await response.json()

        if (abortController.signal.aborted) {
          return
        }

        if (data.error) {
          setError(data.error)
        } else if (data.UserSettings) {
          setUserSettings(data.UserSettings)
          setAuthenticated(data.UserSettings.authenticated)
        }
      } catch (err) {
        if (err && /** @type {any} */ (err).name === 'AbortError') {
          return
        }
        console.error('Error loading user settings:', err)
        if (!abortController.signal.aborted) {
          setError(err instanceof Error ? err.message : 'Failed to load user settings')
        }
      } finally {
        if (!abortController.signal.aborted) {
          setLoading(false)
        }
      }
    }

    loadSettings()

    return () => {
      abortController.abort()
    }
  }, [])

  /** @type {AuthContextValue} */
  const contextValue = {
    loading,
    authenticated,
    userSettings,
    error,
    reload: async () => {
      window.location.reload()
    },
  }

  return <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>
}

export default AuthContextProvider
